import { Paper, Skeleton } from '@mantine/core';
import DOMPurify from 'dompurify';
import { AnimatePresence, motion } from 'framer-motion';
import { type FC, memo } from 'react';
import styles from '@/pages/AgentTraining/components/QuestionCard/QuestionCard.module.css';
import type { TQuestionStep } from '@/types/common';
import { cn } from '@/utils';

interface QuestionCardUnifiedProps {
	step: TQuestionStep;
	isMiniCreatorMode: boolean;
	isPageMode: boolean;
	isOverlayMode: boolean;
}

const processText = (text: string) => text.replace(/\\n/g, '<br>');

const QuestionCard: FC<QuestionCardUnifiedProps> = memo(
	({ step, isMiniCreatorMode, isPageMode, isOverlayMode }) => {
		const { question, answer, isLoading = false } = step;

		return (
			<Paper
				className={cn(styles.card, 'relative w-full h-full', {
					'p-[24px]': isPageMode,
					'p-[20px]': isOverlayMode,
					'flex flex-col justify-center': isMiniCreatorMode
				})}
				component="div"
				radius="md"
			>
				<AnimatePresence mode="wait">
					{isLoading ? (
						<motion.div
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							initial={{ opacity: 0 }}
							key="skeleton"
							transition={{ duration: 0.25 }}
						>
							<Skeleton
								height={isMiniCreatorMode ? 8 : 14}
								mb="sm"
								width="60%"
							/>
							<Skeleton
								height={isMiniCreatorMode ? 10 : 18}
								mb="xs"
							/>
							<Skeleton
								height={isMiniCreatorMode ? 10 : 18}
								mb="xs"
							/>
							<Skeleton
								height={isMiniCreatorMode ? 10 : 18}
								width="70%"
							/>
						</motion.div>
					) : (
						<motion.div
							animate={{ opacity: 1, y: 0 }}
							initial={{ opacity: 0, y: 6 }}
							key="content"
							transition={{ duration: 0.35, ease: 'easeOut' }}
						>
							<div
								className={cn(
									'break-words text-left text-gray-500 text-xs leading-tight mb-2.5 capitalize ![font-family:var(--font-body)]',
									{ 'text-[10px]': isMiniCreatorMode }
								)}
							>
								{question?.replace(/_/g, ' ')}
							</div>

							<div
								className={cn(
									'!break-words ![font-family:var(--font-body)] !max-w-full w-full',
									'overflow-hidden text-left line-clamp-3 whitespace-pre-wrap text-lg',
									{
										'text-[10px] leading-tight': isMiniCreatorMode
									}
								)}
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(processText(answer || ''))
								}}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</Paper>
		);
	}
);

export default QuestionCard;
